import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";
import { SoundControls } from "@/components/SoundControls";

const SilhouetteGauntlet = lazy(() => import("@/components/games/SilhouetteGauntlet").then((m) => ({ default: m.SilhouetteGauntlet })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Who's That Palworld Pal? Free Silhouette Quiz";
const DESCRIPTION =
  "Play the free Who's That Pal? quiz: name ten blacked-out Palworld Pals from four choices, keep your streak alive and share a spoiler-free result grid.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Start the gauntlet", text: "Press start to begin a ten-round run; the first silhouette appears immediately." },
  { name: "Read the shape", text: "Each Pal is a pure black silhouette of its official cut-out artwork on a bright plate — look at wings, horns and tail length." },
  { name: "Pick from four names", text: "Choose the matching Pal name; the answer is revealed for a moment before the next round." },
  { name: "Share the grid", text: "At the end you get an emoji grid of hits and misses plus a share link that reveals no answers." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Who's That Pal? quiz?", a: "It is a free Palworld silhouette quiz on Pal Stars: ten rounds, one blacked-out Pal per round and four names to choose from. A run takes under a minute and your best score is kept in your browser." },
  { q: "Where do the silhouettes come from?", a: "Each silhouette is the Pal's official cut-out artwork rendered fully black on a bright plate, so the outline, horns, wings and tail stay readable while colors and markings are hidden. Pals that only have a rectangular card scan are excluded, because blacking one out would leave an unidentifiable box." },
  { q: "Does the Palworld silhouette quiz give rewards?", a: "No packs or spins — the quiz is cosmetic. You earn achievement badges at 5 correct and for a perfect 10 out of 10, and those badges live in your browser." },
  { q: "How do you stop score cheating?", a: "A run is only stored when it passes the sanity guards: no more hits than rounds and at least 250 milliseconds per round, plus a cap of 25 runs per browser per UTC day." },
  { q: "Can I share my silhouette quiz score?", a: "Yes. The share button copies a spoiler-free emoji grid with your score and a referral link back to the quiz, ready for Discord, X, TikTok or Reddit." },
];

export const Route = createFileRoute("/whos-that-pal")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Who's That Pal? — free Palworld silhouette quiz" },
      { property: "og:description", content: "Ten blacked-out Palworld Pals, four names each, one minute — share your emoji score grid." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/whos-that-pal" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/whos-that-pal" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Who's That Pal?", "/whos-that-pal"]]),
      toolSchema({ name: "Who's That Pal? silhouette quiz", description: DESCRIPTION, path: "/whos-that-pal" }),
      howToSchema({ name: "How to play the Palworld silhouette quiz", description: "Start a ten-round run, read each silhouette, pick the right name and share the grid.", path: "/whos-that-pal", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/whos-that-pal"),
    ],
  }),
  component: SilhouettePage,
});

function SilhouettePage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · 60-second game</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Who's That Palworld Pal?</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Who's That Pal? is a free Palworld silhouette quiz: ten blacked-out Pals, four names per round and a shareable
          emoji grid at the end. Best scores are saved in your browser and no account is needed to play.
        </p>
        <SoundControls className="mt-4 justify-start" label="quiz sound" />
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Silhouette gauntlet</h2><p className="mt-2 text-sm text-muted-foreground">Loading the gauntlet…</p></section>}>
        <SilhouetteGauntlet />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="whos-that-pal" heading="Silhouette quiz badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/daily-puzzle" className="font-semibold text-primary hover:underline">Palworld Pal Puzzle →</Link> daily game with spin rewards.</li>
          <li><Link to="/weekly-tournament" className="font-semibold text-primary hover:underline">Weekly arcade tournament →</Link></li>
          <li><Link to="/type-duel" className="font-semibold text-primary hover:underline">Palworld type chart quiz →</Link></li>
          <li><Link to="/pals" className="font-semibold text-primary hover:underline">Palworld Pal database →</Link></li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld silhouette quiz FAQ" />
    </div>
  );
}
